import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
	Button,
	Caption,
	StatusChip,
	WarnChip,
	InfoChip
} from '@components';
import {
	BUTTON_TYPES,
	BUTTON_SIZE,
	CHIP_TYPE,
	CHIP_SIZE,
	CarrierResult
} from '@types';
import { EfficiencyModuleOpen, ActiveModuleId, EfficiencyFocusRef } from '@state';
import { useTransportShipmentNavigation } from '@hooks';
import {
	locLabel,
	toDateStr,
	getPallets,
	getSpaces,
	getCargo
} from '@utils';
import styles from './ModuleCarrierRecommendation.module.scss';

type CarrierCardProps = {
	result: CarrierResult;
	efficiencyRefs: Set<string>;
};

export const CarrierCard = ({ result, efficiencyRefs }: CarrierCardProps) => {
	const [expanded, setExpanded] = useState(false);
	const bestPriority = result.matches[0]?.priority;
	const setEfficiencyOpen = useSetRecoilState(EfficiencyModuleOpen);
	const setActiveModuleId = useSetRecoilState(ActiveModuleId);
	const setEfficiencyFocusRef = useSetRecoilState(EfficiencyFocusRef);
	const { goToShipment } = useTransportShipmentNavigation();

	const handleConsolidationClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setEfficiencyOpen(true);
		setActiveModuleId('efficiency');
		setEfficiencyFocusRef(result.ref);
	};

	return (
		<div className={styles.card}>
			<div className={styles.cardHeader} onClick={() => setExpanded((e) => !e)}>
				<div>
					<div className={styles.cardRef}>
						{result.ref}
						{result.matches.some((m) => m.flags.length) && (
							<WarnChip text='flags' size={CHIP_SIZE.SMALL} />
						)}
						{efficiencyRefs.has(result.ref) && (
							<button className={styles.chipBtn} onClick={handleConsolidationClick}>
								<InfoChip text='Consolidation potential' size={CHIP_SIZE.SMALL} />
							</button>
						)}
					</div>
					<Caption>
						{result.shipper !== '—' ? <>{result.shipper} · </> : null}
						{locLabel(result.pickupLoc)} · {toDateStr(result.pickupDate)} · {result.temp || '—'} · {result.truckType}
					</Caption>
					<Caption color='var(--neutral-05)'>
						{getCargo(result.shipment)} · {getPallets(result.shipment)} pallets · {getSpaces(result.shipment)} spaces
					</Caption>
				</div>
				<StatusChip
					text={`${result.matches.length} carrier${result.matches.length !== 1 ? 's' : ''}`}
					type={bestPriority?.chipType ?? CHIP_TYPE.NEUTRAL}
					size={CHIP_SIZE.SMALL}
				/>
			</div>

			{expanded && (
				<div className={styles.cardBody}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Carrier</th>
								<th>Shipment nearby</th>
								<th>Cargo type</th>
								<th>Pallets</th>
								<th>Spaces</th>
								<th>Best match</th>
								<th>Temp</th>
								<th>Truck</th>
								<th>Flags</th>
								<th>Allocate</th>
							</tr>
						</thead>
						<tbody>
							{result.matches.map((m, i) => (
								<tr key={i} className={styles.carrierRow}>
									<td><Caption>{m.carrier || '—'}</Caption></td>
									<td>
										<div className={styles.mono}>{m.shipRef}</div>
										<div className={styles.subMeta}>{locLabel(m.dropoffLoc)} · {toDateStr(m.dropoffDate)}</div>
									</td>
									<td className={styles.smallCell}>{m.cargoType}</td>
									<td className={styles.smallCell}>{m.pallets}</td>
									<td className={styles.smallCell}>{m.spaces}</td>
									<td className={styles.smallCell}>
										<StatusChip
											text={m.priority.label}
											type={m.priority.chipType}
											size={CHIP_SIZE.SMALL}
										/>
									</td>
									<td className={styles.smallCell}>{m.temp}</td>
									<td className={styles.smallCell}>{m.truckType}</td>
									<td className={styles.smallCell}>
										{m.flags.length
											? m.flags.map((f) => f.replace('⚠ ', '')).join(', ')
											: '—'}
									</td>
									<td className={styles.smallCell}>
										<Button
											text='Allocate'
											onClick={() => goToShipment(result.shipment, true)}
											type={BUTTON_TYPES.TERTIARY}
											size={BUTTON_SIZE.SMALL}
											Icon={OpenInNewIcon}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
